import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import Tooltip from "@material-ui/core/Tooltip";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import Schedule from "@material-ui/icons/Schedule";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import CakeIcon from "@material-ui/icons/Cake";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  AllInclusive,
  AttachFile,
  Dashboard,
  Description,
  DeviceHubOutlined,
  GridOn,
  PhonelinkSetup,
} from "@material-ui/icons";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import { i18n } from "../translate/i18n";
import { Campaign, ShapeLine, Webhook } from "@mui/icons-material";
import useCompanySettings from "../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  navItem: {
    color: theme.palette.headerText,
    opacity: 0.88,
    borderRadius: 6,
    padding: 7,
    transition: "background 0.2s ease",
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.15)" : "rgba(232,168,158,0.12)",
      opacity: 1,
      color: theme.palette.headerText,
    },
  },
  navItemActive: {
    backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.18)" : "rgba(232,168,158,0.15)",
    opacity: 1,
    color: theme.palette.headerText,
  },
  menuItem: {
    minWidth: 200,
    fontSize: "0.85rem",
    borderRadius: 6,
    margin: "1px 4px",
    padding: "6px 10px",
    fontWeight: 400,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}12`,
    },
  },
  menuItemActive: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: `${theme.palette.primary.main}10`,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  },
  menuItemIcon: {
    minWidth: 32,
    color: "#9ca3af",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];
    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }
  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") return [];
  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) return action.payload.chat;
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const location = useLocation();

  const [anchorEls, setAnchorEls] = useState({});
  const openMenu = (id) => (e) => setAnchorEls((p) => ({ ...p, [id]: e.currentTarget }));
  const closeMenu = (id) => () => setAnchorEls((p) => ({ ...p, [id]: null }));

  const menuProps = (id) => ({
    anchorEl: anchorEls[id],
    open: Boolean(anchorEls[id]),
    onClose: closeMenu(id),
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: {
      style: { borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 210, padding: "4px 0" },
    },
  });

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [invisible, setInvisible] = useState(true);
  const [pageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);
  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);
  const { getPlanCompany } = usePlans();

  // Active route detection
  const atendActive = ["/tickets", "/quick-messages", "/contacts", "/groups", "/schedules", "/tags", "/chats", "/call-historicals"].some(
    (r) => location.pathname.startsWith(r)
  );
  const campActive = ["/campaigns", "/contact-lists", "/campaigns-config"].some(
    (r) => location.pathname.startsWith(r)
  );
  const autoActive = ["/sdr", "/phrase-lists", "/flowbuilders", "/plugins/floup"].some(
    (r) => location.pathname.startsWith(r)
  );
  const gestaoActive =
    location.pathname === "/" ||
    ["/reports", "/moments", "/wallets"].some((r) => location.pathname.startsWith(r));
  const adminActive = [
    "/users", "/connections", "/queues", "/settings", "/financeiro",
    "/files", "/prompts", "/queue-integration", "/announcements",
    "/companies", "/birthday-settings", "/messages-api", "/allConnections",
  ].some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({ column: "DirectTicketsToWallets" });
        setShowWallets(setting.DirectTicketsToWallets);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === "function") {
      const companyId = user.companyId;
      const onCompanyChat = (data) => {
        if (data.action === "new-message") dispatch({ type: "CHANGE_CHAT", payload: data });
        if (data.action === "update") dispatch({ type: "CHANGE_CHAT", payload: data });
      };
      socket.on(`company-${companyId}-chat`, onCompanyChat);
      return () => {
        if (socket && typeof socket.off === "function") {
          socket.off(`company-${companyId}-chat`, onCompanyChat);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) unreadsCount += chatUser.unreads;
        }
      }
    }
    setInvisible(unreadsCount === 0);
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((w) =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        );
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const navBtn = (id, isActive) =>
    `${classes.navItem} ${isActive ? classes.navItemActive : ""}`;

  const menuItem = (to, id, icon, label, isActive = false) => (
    <MenuItem
      component={RouterLink}
      to={to}
      onClick={closeMenu(id)}
      className={`${classes.menuItem} ${isActive ? classes.menuItemActive : ""}`}
    >
      <ListItemIcon className={classes.menuItemIcon}>{icon}</ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  );

  return (
    <div style={{ display: "flex", alignItems: "center" }}>

      {/* ATENDIMENTO */}
      <Tooltip title="Atendimento" placement="bottom">
        <IconButton size="small" onClick={openMenu("atend")} className={navBtn("atend", atendActive)}>
          <WhatsAppIcon />
        </IconButton>
      </Tooltip>
      <Menu {...menuProps("atend")}>
        {menuItem("/tickets", "atend", <WhatsAppIcon fontSize="small" />, i18n.t("mainDrawer.listItems.tickets"), location.pathname.startsWith("/tickets"))}
        {menuItem("/quick-messages", "atend", <FlashOnIcon fontSize="small" />, i18n.t("mainDrawer.listItems.quickMessages"), location.pathname.startsWith("/quick-messages"))}
        {user.showContacts === "enabled" && menuItem("/contacts", "atend", <ContactPhoneOutlinedIcon fontSize="small" />, i18n.t("mainDrawer.listItems.contacts"), location.pathname.startsWith("/contacts"))}
        {menuItem("/groups", "atend", <PeopleIcon fontSize="small" />, "Grupos", location.pathname.startsWith("/groups"))}
        {showSchedules && menuItem("/schedules", "atend", <Schedule fontSize="small" />, i18n.t("mainDrawer.listItems.schedules"), location.pathname.startsWith("/schedules"))}
        {menuItem("/tags", "atend", <LocalOfferIcon fontSize="small" />, i18n.t("mainDrawer.listItems.tags"), location.pathname.startsWith("/tags"))}
        {showInternalChat && (
          <MenuItem
            component={RouterLink}
            to="/chats"
            onClick={closeMenu("atend")}
            className={`${classes.menuItem} ${location.pathname.startsWith("/chats") ? classes.menuItemActive : ""}`}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <Badge color="secondary" variant="dot" invisible={invisible}>
                <ForumIcon fontSize="small" />
              </Badge>
            </ListItemIcon>
            <ListItemText primary={i18n.t("mainDrawer.listItems.chats")} />
          </MenuItem>
        )}
        {showWavoipCall && menuItem("/call-historicals", "atend", <HistoryToggleOffIcon fontSize="small" />, i18n.t("mainDrawer.listItems.callHistorical"), location.pathname.startsWith("/call-historicals"))}
      </Menu>

      {/* CAMPANHAS */}
      {user?.showCampaign === "enabled" && showCampaigns && (
        <>
          <Tooltip title={i18n.t("mainDrawer.listItems.campaigns")} placement="bottom">
            <IconButton size="small" onClick={openMenu("camp")} className={navBtn("camp", campActive)}>
              <Campaign />
            </IconButton>
          </Tooltip>
          <Menu {...menuProps("camp")}>
            {menuItem("/campaigns", "camp", <ListIcon fontSize="small" />, i18n.t("campaigns.subMenus.list"), location.pathname === "/campaigns")}
            {menuItem("/contact-lists", "camp", <PeopleIcon fontSize="small" />, i18n.t("campaigns.subMenus.listContacts"), location.pathname.startsWith("/contact-lists"))}
            {menuItem("/campaigns-config", "camp", <SettingsOutlinedIcon fontSize="small" />, i18n.t("campaigns.subMenus.settings"), location.pathname.startsWith("/campaigns-config"))}
          </Menu>
        </>
      )}

      {/* AUTOMAÇÕES */}
      {user.showFlow === "enabled" && (
        <>
          <Tooltip title={i18n.t("mainDrawer.listItems.automations")} placement="bottom">
            <IconButton size="small" onClick={openMenu("auto")} className={navBtn("auto", autoActive)}>
              <Webhook />
            </IconButton>
          </Tooltip>
          <Menu {...menuProps("auto")}>
            {menuItem("/sdr", "auto", <AutoFixHighIcon fontSize="small" />, "Agente SDR", location.pathname.startsWith("/sdr"))}
            {menuItem("/phrase-lists", "auto", <EventAvailableIcon fontSize="small" />, "Fluxo de Campanha", location.pathname.startsWith("/phrase-lists"))}
            {menuItem("/flowbuilders", "auto", <ShapeLine fontSize="small" />, "Fluxo de Conversa", location.pathname.startsWith("/flowbuilders"))}
            {menuItem("/plugins/floup", "auto", <Webhook fontSize="small" />, "Follow UP (Templates)", location.pathname.startsWith("/plugins/floup"))}
          </Menu>
        </>
      )}

      {/* GESTÃO */}
      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") || user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Tooltip title={i18n.t("mainDrawer.listItems.management")} placement="bottom">
              <IconButton size="small" onClick={openMenu("gestao")} className={navBtn("gestao", gestaoActive)}>
                <Dashboard />
              </IconButton>
            </Tooltip>
            <Menu {...menuProps("gestao")}>
              <Can
                role={user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
                perform="drawer-admin-items:view"
                yes={() => (
                  <>
                    {menuItem("/", "gestao", <DashboardOutlinedIcon fontSize="small" />, "Dashboard", location.pathname === "/")}
                    {menuItem("/reports", "gestao", <Description fontSize="small" />, i18n.t("mainDrawer.listItems.reports"), location.pathname.startsWith("/reports"))}
                  </>
                )}
              />
              <Can
                role={user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
                perform="drawer-admin-items:view"
                yes={() =>
                  menuItem("/moments", "gestao", <GridOn fontSize="small" />, i18n.t("mainDrawer.listItems.chatsTempoReal"), location.pathname.startsWith("/moments"))
                }
              />
              {user.profile === "admin" && menuItem("/plugins/floup/dashboard", "gestao", <DashboardOutlinedIcon fontSize="small" />, "Painel FollowUP", location.pathname === "/plugins/floup/dashboard")}
              {user.profile === "admin" && showWallets && menuItem("/wallets", "gestao", <AccountBalanceWalletIcon fontSize="small" />, i18n.t("mainDrawer.listItems.wallets"), location.pathname.startsWith("/wallets"))}
            </Menu>
          </>
        )}
      />

      {/* ADMINISTRAÇÃO */}
      <Can
        role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
        perform="dashboard:view"
        yes={() => (
          <>
            <Tooltip title={connectionWarning ? "⚠️ Conexão com problema — clique para verificar" : i18n.t("mainDrawer.listItems.administration")} placement="bottom">
              <IconButton size="small" onClick={openMenu("admin")} className={navBtn("admin", adminActive)}>
                <Badge color="error" variant="dot" invisible={!connectionWarning}>
                  <SettingsOutlinedIcon style={connectionWarning ? { color: "#ef4444" } : {}} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu {...menuProps("admin")}>
              {(user.super || user.profile === "admin") && menuItem("/announcements", "admin", <AnnouncementIcon fontSize="small" />, i18n.t("mainDrawer.listItems.annoucements"), location.pathname.startsWith("/announcements"))}
              {showExternalApi && (
                <Can role={user.profile} perform="dashboard:view" yes={() =>
                  menuItem("/messages-api", "admin", <CodeRoundedIcon fontSize="small" />, i18n.t("mainDrawer.listItems.messagesAPI"), location.pathname.startsWith("/messages-api"))
                } />
              )}
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/users", "admin", <PeopleAltOutlinedIcon fontSize="small" />, i18n.t("mainDrawer.listItems.users"), location.pathname.startsWith("/users"))
              } />
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/birthday-settings", "admin", <CakeIcon fontSize="small" />, i18n.t("mainDrawer.listItems.birthdaySettings"), location.pathname.startsWith("/birthday-settings"))
              } />
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/queues", "admin", <AccountTreeOutlinedIcon fontSize="small" />, i18n.t("mainDrawer.listItems.queues"), location.pathname.startsWith("/queues"))
              } />
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/files", "admin", <AttachFile fontSize="small" />, i18n.t("mainDrawer.listItems.files"), location.pathname.startsWith("/files"))
              } />
              {showOpenAi && (
                <Can role={user.profile} perform="dashboard:view" yes={() =>
                  menuItem("/prompts", "admin", <AllInclusive fontSize="small" />, i18n.t("mainDrawer.listItems.prompts"), location.pathname.startsWith("/prompts"))
                } />
              )}
              {showIntegrations && (
                <Can role={user.profile} perform="dashboard:view" yes={() =>
                  menuItem("/queue-integration", "admin", <DeviceHubOutlined fontSize="small" />, i18n.t("mainDrawer.listItems.queueIntegration"), location.pathname.startsWith("/queue-integration"))
                } />
              )}
              <Divider style={{ margin: "4px 0" }} />
              <Can
                role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                perform="drawer-admin-items:view"
                yes={() => (
                  <MenuItem
                    component={RouterLink}
                    to="/connections"
                    onClick={closeMenu("admin")}
                    className={`${classes.menuItem} ${location.pathname.startsWith("/connections") ? classes.menuItemActive : ""}`}
                  >
                    <ListItemIcon className={classes.menuItemIcon}>
                      <Badge color="error" variant="dot" invisible={!connectionWarning}>
                        <SyncAltIcon fontSize="small" />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary={i18n.t("mainDrawer.listItems.connections")} />
                  </MenuItem>
                )}
              />
              {user.super && menuItem("/allConnections", "admin", <PhonelinkSetup fontSize="small" />, i18n.t("mainDrawer.listItems.allConnections"), location.pathname.startsWith("/allConnections"))}
              <Divider style={{ margin: "4px 0" }} />
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/financeiro", "admin", <LocalAtmIcon fontSize="small" />, i18n.t("mainDrawer.listItems.financeiro"), location.pathname.startsWith("/financeiro"))
              } />
              <Can role={user.profile} perform="dashboard:view" yes={() =>
                menuItem("/settings", "admin", <SettingsOutlinedIcon fontSize="small" />, i18n.t("mainDrawer.listItems.settings"), location.pathname.startsWith("/settings"))
              } />
              {user.super && menuItem("/companies", "admin", <BusinessIcon fontSize="small" />, i18n.t("mainDrawer.listItems.companies"), location.pathname.startsWith("/companies"))}
            </Menu>
          </>
        )}
      />

      {/* AJUDA */}
      {hasHelps && (
        <Tooltip title={i18n.t("mainDrawer.listItems.helps")} placement="bottom">
          <IconButton
            size="small"
            component={RouterLink}
            to="/helps"
            className={`${classes.navItem} ${location.pathname.startsWith("/helps") ? classes.navItemActive : ""}`}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export default MainListItems;
